import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Select, Store } from '@ngxs/store';
import { ConfirmBancontactPaymentData, ConfirmCardPaymentData } from '@stripe/stripe-js';
import { StripeFactoryService, StripeInstance } from 'ngx-stripe';
import { Observable } from 'rxjs';
import { switchMap, tap, withLatestFrom } from 'rxjs/operators';

import { BaseComponent } from '../../abstracts/base.abstract';
import { BasicInputTypeEnum } from '../../enums/basic-input-type.enum';
import { SuccessErrorEnum } from '../../enums/success-error.enum';
import { ISchool } from '../../interfaces/groups/school.interface';
import { IBancontactPaymentIntentDtoOut, IPaymentIntentDtoOut } from '../../interfaces/stripe.interface';
import { IUser } from '../../interfaces/users/user.interface';
import { SetRememberMe } from '../../store/actions/auth.action';
import { ConfirmPayment, CreatePaymentIntent, ResetErrorStripe } from '../../store/actions/stripe.action';
import { GetWallets } from '../../store/actions/wallet.action';
import { AuthState } from '../../store/state/auth.state';
import { StripeState } from '../../store/state/stripe.state';
import { StripeCardFormComponent } from '../stripe-card-form/stripe-card-form.component';

export enum MeanOfPaymentEnum {
    BANCONTACT = 'bancontact',
    CREDIT_CARD = 'credit_card',
}

@Component({
    selector: 'app-stripe',
    templateUrl: './stripe.component.html',
    styleUrls: ['./stripe.component.scss'],
})
export class StripeComponent extends BaseComponent implements OnInit, OnChanges {
    @Select(AuthState.currentSchool) public currentSchool$: Observable<ISchool>;
    @Select(AuthState.user) public connectedUser$: Observable<IUser>;
    @Select(StripeState.isInTransaction) public isInTransaction$: Observable<boolean>;
    @Select(StripeState.paymentError) public paymentError$: Observable<string>;

    @ViewChild(StripeCardFormComponent) public stripeCardFormComponent: StripeCardFormComponent;

    @Input() public amount: number = 0;

    @Output() public paymentStatus = new EventEmitter<SuccessErrorEnum>();

    public connectedUser: IUser;

    public stripe: StripeInstance;
    public isInTransaction: boolean;
    public stripeConfirmation: SuccessErrorEnum;
    public reloadForm: FormGroup;
    public basicInputTypeEnum = BasicInputTypeEnum;
    public stripeStatusEnum = SuccessErrorEnum;

    public isLoading = false;

    public meanOfPaymentEnum = MeanOfPaymentEnum;
    public meanOfPayment: MeanOfPaymentEnum = MeanOfPaymentEnum.BANCONTACT;

    public get reloadAmount(): FormControl {
        return this.reloadForm?.get('amount') as FormControl;
    }

    constructor(
        private fb: FormBuilder,
        private store: Store,
        private stripeFactory: StripeFactoryService,
        private translate: TranslateService
    ) {
        super();
        this.createRechargeForm();
    }

    ngOnInit(): void {
        this.uns = this.connectedUser$.subscribe((connectedUser: IUser) => {
            this.connectedUser = connectedUser;
        });

        this.uns = this.currentSchool$.subscribe((school: ISchool) => {
            if (school && school.stripeSettings && school.stripeSettings.publishable_key) {
                this.stripe = this.stripeFactory.create(school.stripeSettings.publishable_key);
            }
        });

        this.uns = this.isInTransaction$.subscribe((isInTransaction: boolean) => (this.isInTransaction = isInTransaction));
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes && changes.amount.currentValue && changes.amount.currentValue !== 0 && this.reloadAmount) {
            this.reloadAmount.patchValue(changes.amount.currentValue);
        }
    }

    public payBancontact(): void {
        const statementDescription: string = this.translate.instant('SHOP.SHARED.STRIPE.BANCONTACT_STATEMENT_DESCRIPTION');
        const paymentIntent: IBancontactPaymentIntentDtoOut = {
            payment_intent: {
                amount: Math.round(+this.reloadAmount.value * 100),
                name: this.connectedUser.firstname + ' ' + this.connectedUser.lastname,
                confirm: true,
                currency: 'eur',
                statement_descriptor: statementDescription,
                payment_method: 'bancontact',
                description: statementDescription,
            },
        };
        this.isLoading = true;
        this.store
            .dispatch(new CreatePaymentIntent(paymentIntent))
            .pipe(
                tap(() => {
                    const data: ConfirmBancontactPaymentData = {
                        return_url: window.location.href,
                        payment_method: {
                            billing_details: {
                                name: this.connectedUser.firstname + ' ' + this.connectedUser.lastname,
                            },
                        },
                        mandate_data: {
                            statement_descriptor: statementDescription,
                        },
                    };
                    if (!this.store.selectSnapshot(AuthState.rememberMe)) {
                        // set auth.state.remeberMe = true to ensure auto-login when landing back from stripe
                        // + added flag param to reset it after landing
                        data.return_url = data.return_url + '?auto_login=true';
                        this.store.dispatch(new SetRememberMe());
                    }
                    this.stripe
                        .confirmBancontactPayment(this.store.selectSnapshot(StripeState.paymentIntent).client_secret, data)
                        .subscribe();
                }),
                withLatestFrom(this.paymentError$)
            )
            .subscribe(([, error]: [any, string]) => {
                if (error) {
                    this.stripeConfirmation = SuccessErrorEnum.ERROR;
                    this.isLoading = false;
                    this.paymentStatus.emit(this.stripeConfirmation);
                    return;
                }
                this.isLoading = false;
            });
    }

    // STRIPE
    public startPayment(): void {
        const paymentIntent: IPaymentIntentDtoOut = {
            payment_intent: {
                amount: Math.round(+this.reloadAmount.value * 100),
            },
        };
        this.isLoading = true;
        this.store
            .dispatch(new CreatePaymentIntent(paymentIntent))
            .pipe(
                switchMap(() => {
                    const data: ConfirmCardPaymentData = {
                        payment_method: {
                            card: this.stripeCardFormComponent.card.element,
                            billing_details: {
                                name: this.connectedUser.firstname + ' ' + this.connectedUser.lastname,
                            },
                        },
                    };
                    return this.store.dispatch(new ConfirmPayment(this.stripe, data));
                }),
                withLatestFrom(this.paymentError$)
            )
            .subscribe(([, error]: [any, string]) => {
                if (error) {
                    this.stripeConfirmation = SuccessErrorEnum.ERROR;
                    this.isLoading = false;
                    this.paymentStatus.emit(this.stripeConfirmation);
                    return;
                }
                this.stripeConfirmation = SuccessErrorEnum.SUCCESS;
                setTimeout(() => {
                    this.store.dispatch(new GetWallets()).subscribe(() => {
                        this.isLoading = false;
                        this.paymentStatus.emit(this.stripeConfirmation);
                    });
                }, 5000);
            });
    }

    private createRechargeForm(): void {
        this.reloadForm = this.fb.group({
            amount: ['', [Validators.required, Validators.min(0.5)]],
            transaction: [2, Validators.required],
        });
        this.uns = this.reloadForm.valueChanges.subscribe(() => {
            if (this.store.selectSnapshot(StripeState.showGenericError)) {
                this.store.dispatch(new ResetErrorStripe());
            }
        });
    }
}
